import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import SplashScreen from './components/SplashScreen';
import MainLayout from './components/MainLayout';
import AuthCallback from './components/AuthCallback';
import { useAuth } from './hooks/useAuth';
import eventStreamService from './services/eventStreamService';
import pollingService from './services/pollingService';
import * as feedSync from './services/feedSync';
import { drainOutbox } from './services/outboxDrain';
import lexiconService from './services/lexiconService';
import API_BASE_URL, { APP_ENV, HOME_ROUTE } from './config';
import { maybeReloadIfUpdated } from './services/versionGuard';
import './utils/loggingConfig'; // Initialize logging configuration
import BugReportModal from './components/BugReportModal';
import { TutorialProvider } from './tutorial/TutorialContext';
import * as hazardGateway from './db/hazardGateway';

/** Verbose App.tsx diagnostics — omit in production to reduce console noise and work. */
const appDebug = (...args: unknown[]) => {
  if (APP_ENV === 'dev') {
    console.log(...args);
  }
};

const LoginScreen: React.FC = () => {
  const { login, isLoginInProgress } = useAuth();
  const [isBugOpen, setIsBugOpen] = React.useState(false);
  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
      {APP_ENV === 'dev' && <p>DEV INSTANCE</p>}
      <SplashScreen /> {/* Background Animation */}
      <div style={{ position: 'absolute', top: '70%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <div className="flex flex-col space-y-4 items-center">
          <button
            onClick={() => login()}
            disabled={isLoginInProgress}
            className={`font-bold py-3 px-8 rounded-full shadow-lg transition-colors duration-200 ${
              isLoginInProgress 
                ? 'bg-gray-300 cursor-not-allowed' 
                : 'bg-white hover:bg-blue-100'
            }`}
            style={{ color: isLoginInProgress ? '#999' : '#FF3EB5' }}
          >
            {isLoginInProgress ? 'Logging in...' : 'Login'}
          </button>
          <button
            onClick={() => setIsBugOpen(true)}
            className="text-white bg-[#FF3EB5] hover:bg-[#E635A3] font-medium py-2 px-4 rounded-full"
          >
            Report a bug
          </button>
        </div>
      </div>
      <BugReportModal isOpen={isBugOpen} onClose={() => setIsBugOpen(false)} />
    </div>
  );
};

const App: React.FC = () => {
  const { isAuthenticated, isLoading, oidcConfig, userInfo } = useAuth();
  const [storeReady, setStoreReady] = useState(false);

  // Initialize dark mode theme on app start
  useEffect(() => {
    // Check backend version to avoid stale caches after deployment
    maybeReloadIfUpdated();

    const initializeTheme = () => {
      const savedTheme = localStorage.getItem('theme');
      let isDarkMode = false;
      
      if (savedTheme) {
        isDarkMode = savedTheme === 'dark';
      } else {
        // Default to the deck dark theme on first load, and persist it so the
        // Settings toggle reflects the same state.
        isDarkMode = true;
        localStorage.setItem('theme', 'dark');
      }
      
      const root = window.document.documentElement;
      if (isDarkMode) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    initializeTheme();
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!isAuthenticated || !userInfo?.userId) {
      setStoreReady(true);
      return;
    }
    let cancelled = false;
    setStoreReady(false);
    hazardGateway.ensureUserStoreReady(userInfo.userId)
      .then(() => {
        if (!cancelled) {
          setStoreReady(true);
        }
      })
      .catch((error) => {
        appDebug('Failed to open the per-user store', error);
        if (!cancelled) {
          setStoreReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, userInfo?.userId]);

  // Initialize realtime service when user is authenticated
  // PERFORMANCE: Defer initialization to allow page to render first
  useEffect(() => {
    let idleCallbackId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    
    if (isAuthenticated) {
      // Use requestIdleCallback for non-critical initialization
      const initializeServices = () => {
        appDebug('User authenticated, initializing realtime service...');
        eventStreamService.initialize();
      };
      
      if ('requestIdleCallback' in window) {
        idleCallbackId = requestIdleCallback(initializeServices, { timeout: 2000 });
      } else {
        timeoutId = setTimeout(initializeServices, 2000);
      }
    } else {
      // Disconnect when user logs out
      eventStreamService.disconnect();
    }
    
    // Cleanup: cancel pending callbacks on unmount or auth change
    return () => {
      if (idleCallbackId !== undefined && 'cancelIdleCallback' in window) {
        cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [isAuthenticated]);

  // Hazard polling is fallback only while WebSocket is disconnected / unhealthy.
  useEffect(() => {
    const onConn = (connected: boolean) => {
      pollingService.setRealtimePrimary(connected);
    };
    eventStreamService.addConnectionListener(onConn);
    onConn(eventStreamService.isConnectedToStream());
    return () => {
      eventStreamService.removeConnectionListener(onConn);
    };
  }, []);

  // Initialize polling service when user is authenticated
  // PERFORMANCE: Defer initialization to allow page to render first
  useEffect(() => {
    let idleCallbackId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    
    if (isAuthenticated) {
      const initializePolling = () => {
        appDebug('User authenticated, initializing polling service...');
        pollingService.initialize();
      };
      
      if ('requestIdleCallback' in window) {
        idleCallbackId = requestIdleCallback(initializePolling, { timeout: 2000 });
      } else {
        timeoutId = setTimeout(initializePolling, 2000);
      }
    } else {
      // Stop polling when user logs out
      pollingService.stop();
    }

    // Cleanup: cancel pending callbacks and stop polling on unmount or auth change
    return () => {
      if (idleCallbackId !== undefined && 'cancelIdleCallback' in window) {
        cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      pollingService.stop();
    };
  }, [isAuthenticated]);

  // Global online/offline sync handlers with enhanced reliability
  useEffect(() => {
    // Wait until adopt finishes. The 2s delay only defers work after the store is ready.
    if (!isAuthenticated || !storeReady) {
      return;
    }

    let networkStatusTimer: number | null = null;
    let syncCheckInterval: number | null = null;
    let lastOnlineStatus = navigator.onLine;
    let syncInProgress = false;
    let syncQueued = false;
    let queuedSource: string | null = null;
    
    const triggerSyncIfNeeded = async (source: string) => {
      if (syncInProgress) {
        syncQueued = true;
        queuedSource = source;
        return;
      }

      syncInProgress = true;
      try {
        appDebug(`🔄 ${source}: Triggering sync check...`);
        
        // Clear any pending timer
        if (networkStatusTimer) {
          window.clearTimeout(networkStatusTimer);
        }
        
        // Test connectivity with a small request
        const isReallyOnline = await testConnectivity();
        
        if (isReallyOnline) {
          appDebug('🌐 App: Connection confirmed, triggering immediate sync...');
          
          // Immediate sync of all unsynced data
          await drainOutbox();
          await feedSync.request('full');
        } else {
          appDebug('📴 App: Connection test failed, will retry...');
        }
      } catch (error) {
        console.error('Error during sync trigger:', error);
      } finally {
        syncInProgress = false;
        if (syncQueued) {
          const replaySource = queuedSource ? `Queued: ${queuedSource}` : 'Queued trigger';
          syncQueued = false;
          queuedSource = null;
          // Run once more to coalesce all triggers that fired while sync was in progress.
          void triggerSyncIfNeeded(replaySource);
        }
      }
    };
    
    // Test if we're really online by making a small request
    const testConnectivity = async (): Promise<boolean> => {
      try {
        // Use the health check endpoint to test connectivity
        const response = await fetch(`${API_BASE_URL}/api/db-health-check`, {
          method: 'GET',
          headers:
            typeof window !== 'undefined'
              ? { 'X-Forwarded-Host': window.location.host }
              : undefined,
          signal: AbortSignal.timeout(5000) // 5 second timeout
        });
        return response.ok;
      } catch (error) {
        appDebug('Connectivity test failed:', error);
        return false;
      }
    };
    
    const handleOnline = () => {
      appDebug('Online event detected');
      triggerSyncIfNeeded('Online Event');
    };
    
    const handleOffline = () => {
      appDebug('App: Connection lost, changes will be queued for sync...');
      // Clear any pending timer when going offline
      if (networkStatusTimer) {
        window.clearTimeout(networkStatusTimer);
        networkStatusTimer = null;
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        appDebug('App became visible, checking for sync...');
        triggerSyncIfNeeded('Visibility Change');
      }
    };
    
    // Periodic sync check (every 60 seconds when app is active)
    const checkSyncPeriodically = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        const currentOnlineStatus = navigator.onLine;
        if (currentOnlineStatus !== lastOnlineStatus) {
          appDebug(`Online status changed: ${lastOnlineStatus} -> ${currentOnlineStatus}`);
          lastOnlineStatus = currentOnlineStatus;
          if (currentOnlineStatus) {
            triggerSyncIfNeeded('Status Change Detection');
          }
        } else if (currentOnlineStatus) {
          // Even if status hasn't changed, try sync (handles cases where online event doesn't fire)
          triggerSyncIfNeeded('Periodic Check');
        }
      }
    };

    // Set up event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // PERFORMANCE: Start periodic sync check with longer interval (60s instead of 30s)
    syncCheckInterval = window.setInterval(checkSyncPeriodically, 60000); // Every 60 seconds

    // PERFORMANCE: Defer initial sync to allow page to render first (2s instead of 1s)
    // Reduced from 5s to 2s to ensure timely content updates for user actions
    const initialSyncTimeout = setTimeout(() => {
      triggerSyncIfNeeded('Initial Load');
      lexiconService.syncLexicon();
    }, 2000);

    // Expose manual sync function globally for testing
    (window as any).manualSync = () => {
      appDebug('Manual sync triggered from console');
      triggerSyncIfNeeded('Manual Trigger');
    };

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (networkStatusTimer) {
        window.clearTimeout(networkStatusTimer);
      }
      if (syncCheckInterval) {
        window.clearInterval(syncCheckInterval);
      }
      clearTimeout(initialSyncTimeout);
    };
  }, [isAuthenticated, storeReady]);

  useEffect(() => {
    if (APP_ENV !== 'dev') return;
    console.log('App.tsx - Authentication state changed:', {
      isAuthenticated,
      isLoading,
      currentPath: window.location.pathname
    });
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (APP_ENV !== 'dev') return;
    console.log('Environment debug:', {
      APP_ENV,
      REACT_APP_APP_ENV: process.env.REACT_APP_APP_ENV,
      APP_ENV_process: process.env.APP_ENV,
      runtime_APP_ENV: (window as any).__ENV__?.APP_ENV,
      NODE_ENV: process.env.NODE_ENV
    });
  }, []);



  if (isLoading || (isAuthenticated && !storeReady)) {
    return <SplashScreen />;
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/login" 
          element={!isAuthenticated ? <LoginScreen /> : <Navigate to={HOME_ROUTE} replace />}
        />
        <Route
          path="/auth/callback"
          element={<AuthCallback oidcConfig={oidcConfig} />}
        />
        <Route
          path="/app/*"
          element={isAuthenticated ? (
            <TutorialProvider>
              <MainLayout />
            </TutorialProvider>
          ) : <Navigate to="/login" replace />}
        />
        <Route 
          path="/" 
          element={<Navigate to={isAuthenticated ? HOME_ROUTE : "/login"} replace />}
        />
      </Routes>
    </Router>
  );
};

export default App;